import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Alert, Button, Modal, Spin } from 'antd';
import { fetchCommunityRules, REGISTER_MESSAGES } from './register-api';
import { htmlToReact } from './safe-html';
import styles from './register.module.scss';

interface RulesModalProps {
  open: boolean;
  onClose: () => void;
  /** "Tôi đồng ý": đánh dấu ô đồng ý rồi đóng. */
  onAgree: () => void;
}

type LoadState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; content: ReactNode }
  | { status: 'error' };

/**
 * Quy tắc cộng đồng — cùng nội dung màn "Quy tắc cộng đồng" app mở từ màn đăng ký
 * (GET /rules, công khai). Tải ở lần mở đầu tiên; lỗi thì cho thử lại.
 */
const RulesModal = ({ open, onClose, onAgree }: RulesModalProps) => {
  const [state, setState] = useState<LoadState>({ status: 'idle' });

  const load = useCallback(async () => {
    setState({ status: 'loading' });
    try {
      const html = await fetchCommunityRules();
      setState({ status: 'ready', content: htmlToReact(html) });
    } catch {
      setState({ status: 'error' });
    }
  }, []);

  useEffect(() => {
    if (open && state.status === 'idle') load();
  }, [open, state.status, load]);

  let body: ReactNode;
  if (state.status === 'ready') {
    body = state.content ? (
      <div className={styles.rules}>{state.content}</div>
    ) : (
      <p className={styles.rulesEmpty}>{REGISTER_MESSAGES.rulesEmpty}</p>
    );
  } else if (state.status === 'error') {
    body = (
      <Alert
        type="error"
        showIcon
        message={REGISTER_MESSAGES.rulesFailed}
        action={
          <Button size="small" onClick={load}>
            Thử lại
          </Button>
        }
      />
    );
  } else {
    body = (
      <div className={styles.rulesLoading}>
        <Spin />
        <span>Đang tải Quy tắc cộng đồng…</span>
      </div>
    );
  }

  return (
    <Modal
      open={open}
      title="Quy tắc cộng đồng"
      onCancel={onClose}
      // Nút X mặc định của antd mang tên "Close" (tiếng Anh) với trình đọc màn hình.
      closable={{ 'aria-label': 'Đóng' }}
      width={600}
      centered
      footer={[
        <Button key="close" onClick={onClose}>
          Đóng
        </Button>,
        <Button key="agree" type="primary" onClick={onAgree} disabled={state.status !== 'ready'}>
          Tôi đồng ý
        </Button>,
      ]}
    >
      {/* Vùng cuộn nhận Tab để người dùng bàn phím cuộn được nội dung dài bằng phím mũi tên. */}
      <div
        className={styles.rulesScroll}
        role="region"
        aria-label="Nội dung Quy tắc cộng đồng"
        aria-busy={state.status === 'loading'}
        tabIndex={0}
      >
        {body}
      </div>
    </Modal>
  );
};

export default RulesModal;
